define(['jQuery', 'underscore', 'library/fcse-util/paramConverter'], function ($, _, ParamConverter) {
    /**
     * @param parentScope parent scope
     * @param name name of the scope
     * @param persistent is scope persistent, as in, do we save prefixed parameters in the root scope
     * @param {Object|null} initialScopeParams    An object of initial scope parameters, can be used to pass parameters to backend
     *                  that have no widget on the current page
     * @constructor
     */
    var Scope = function (parentScope, name, persistent, initialScopeParams) {
        this.children = [];
        this.parentScope = parentScope;
        if(this.parentScope) {
            this.parentScope.children.push(this);
        }
        this.name = name;
        this._persistent = !!persistent;
        if (this._persistent && !name) {
            throw "persistent scope must have a name";
        }

        this.params = this._convertInitialParams(initialScopeParams);
        this._changeHandlers = [];
        this._paramChangeHandlers = [];
        this._subscribeToParentParamChange();
        this._subscribeToRootParamChange();
    };

    Scope.prototype = {
        /**
         * Gets all params associated with this scope
         * @returns {*}
         */
        getParams: function () {
            if (this.parentScope) {
                var parentParams = this.parentScope.getParams();
                //we only want persistent parameters to be saved in the root scope
                var copyWithoutPersistent = {};
                for (var key in parentParams) {
                    if (parentParams.hasOwnProperty(key) && key.indexOf('.') < 0) {
                        copyWithoutPersistent[key] = parentParams[key];
                    }
                }
                return $.extend({}, true, copyWithoutPersistent, this.params);
            } else {
                return $.extend({}, true, this.params);
            }
        },
        /**
         * Sets all parameters to this scope
         * @param params
         */
        setParams: function (params) {
            var self = this;
            /*
            INFO: Is removed, to be able to set null (default) values in widgets
            So far needed for URL history ajax handling UrlHandling.enableBrowserHistory
            _.each(params, function(value, key, obj) {
                if ((_.isObject(value) && _.isEmpty(value)) || (obj[key] == null)) {
                    delete obj[key];
                }
            });
            */
            var oldParams = $.extend({}, true, this.params);
            this.params = $.extend({}, true, params);
            var changed = false;

            var oldKeys = _.keys(oldParams);
            var newKeys = _.keys(this.params);
            var common = _.intersection(oldKeys, newKeys);
            var changedParams = [];

            //check for changed parameters
            _.each(common, function (key) {
                if (!_.isEqual(oldParams[key], self.params[key])) {
                    self._setPersistentParam(key, self.params[key]);
                    self._fireParamChangeEvent(key, self.params[key], this);
                    changed = true;
                    changedParams.push(key);
                }
            });
            //check for removed parameters
            _.each(_.difference(oldKeys, common), function (key) {
                self._setPersistentParam(key, null);
                self._fireParamChangeEvent(key, null, this);
                changed = true;
                changedParams.push(key);
            });
            //check for added parameters
            _.each(_.difference(newKeys, common), function (key) {
                self._setPersistentParam(key, self.params[key]);
                self._fireParamChangeEvent(key, self.params[key], this);
                changed = true;
                changedParams.push(key);
            });
            //if a parameter was added/removed or changed, fire a change event
            if (changed) {
                this._fireChangeEvent(this, 'setParams', changedParams);
            }
        },
        /**
         * Converts the initial params to actual param values
         * They need to go through ParamConverter to use travelSearchMetadata
         *
         * @param params
         * @returns {{}}
         * @private
         */
        _convertInitialParams: function(params)
        {
            if (!params){
                return {};
            }

            // Convert initial value to native widget values using ParamConverter
            var convertedParams = {};
            $.each(params, function(name, value){
                ParamConverter.convertParamFromQuery(name, value, convertedParams);
            });

            return convertedParams;
        },
        /**
         * Sets a single parameter to this scope. If value is <code>null</code> then parameter gets removed.
         * @param name
         * @param value
         */
        setParam: function (name, value) {
            this._setPersistentParam(name, value);
            var oldValue = this.params[name];
            if (value != null) {
                this.params[name] = value;
            } else {
                delete this.params[name];
            }
            if (oldValue != value) {
                this._fireParamChangeEvent(name, value, this);
                this._fireChangeEvent(this, 'setParam', [name]);
            }
        },
        /**
         * Sets multiple parameters to this scope.
         * @param params
         */
        setMultipleParams: function (params) {
            var changed = false;
            var changedParams = [];
            for (var name in params) {
                if (params.hasOwnProperty(name)) {
                    var value = params[name];
                    this._setPersistentParam(name, value);
                    var oldValue = this.params[name];
                    if (value != null) {
                        this.params[name] = value;
                    } else {
                        delete this.params[name];
                    }
                    if (oldValue != value) {
                        this._fireParamChangeEvent(name, value, this);
                        changed = true;
                        changedParams.push(name);
                    }
                }
            }
            if (changed) {
                this._fireChangeEvent(this, 'setMultipleParams', changedParams);
            }
        },
        /**
         * Sets multiple parameters to this scope; if parameter is prefixed, set it down to children
         * @param params
         */
        setMultiplePrefixedParams: function (params) {
            var changed = false;
            var changedParams = [];
            //parameters to be sent to children of this scope
            var sendDown = {};
            for (var name in params) {
                if (params.hasOwnProperty(name)) {
                    var value = params[name];
                    var prefixEnd = name.indexOf(".");
                    var prefix = prefixEnd == -1 ? "" : name.substr(0, prefixEnd);
                    if (prefix !== "" && prefix !== this.name) {
                        //if it does not belong to this scope, add it to params to be sent to children
                        sendDown[name] = value;
                    } else if (prefix === "" || prefix === this.name) {
                        //parameter does belong to this scope, set it
                        if(prefix !== "") {
                            //prefixed parameter
                            name = name.substr(prefix.length + 1);
                        }
                        this._setPersistentParam(name, value);
                        var oldValue = this.params[name];
                        if (value != null) {
                            this.params[name] = value;
                        } else {
                            delete this.params[name];
                        }
                        if (oldValue != value) {
                            this._fireParamChangeEvent(name, value, this);
                            changed = true;
                        }
                    }
                    changedParams.push(name);
                }
            }
            if(!_.isEmpty(sendDown) && this.children.length) {
                for (var i = 0; i < this.children.length; i++) {
                    this.children[i].setMultiplePrefixedParams(sendDown);
                }
            }
            if (changed) {
                this._fireChangeEvent(this, 'setMultiplePrefixedParams', changedParams);
            }
        },
        /**
         * Gets a single parameter from this scope
         * @param name
         * @returns {*}
         */
        getParam: function (name) {
            var params = this.getParams();
            if (params.hasOwnProperty(name)) {
                return params[name];
            } else {
                return null;
            }
        },
        /**
         * Adds a change handler for when this scope's parameters change
         * @param handler
         */
        change: function (handler) {
            if (!$.isFunction(handler)) {
                throw "handler must be a function";
            }
            this._changeHandlers[this._changeHandlers.length] = handler;
        },
        /**
         * Adds a param change handler for when this scope's parameter changes
         * @param handler
         */
        paramChange: function (handler) {
            if (!$.isFunction(handler)) {
                throw "handler must be a function";
            }
            this._paramChangeHandlers[this._paramChangeHandlers.length] = handler;
        },
        /**
         * Get encoded params query
         */
        getQuery: function () {
            return ParamConverter.toQuery(this.getParams());
        },
        /**
         * Finds root scope
         * @returns {*}
         * @protected
         */
        _findRoot: function () {
            var root = this;
            //searching for root scope
            while (root.parentScope) {
                root = root.parentScope;
            }
            return root;
        },
        /**
         * Sets persistent param (all the way to root)
         * @param name
         * @param value
         * @private
         */
        _setPersistentParam: function (name, value) {
            if (this._persistent) {
                var root = this._findRoot();
                if (root !== this) {
                    //only need to set prefixed parameter if we are not the root scope
                    //also, we don't want this change to trigger event handler back in this scope,
                    //so, suppressing handling of parent updates
                    root._suppressChildUpdates = true;
                    root.setParam(this.name + '.' + name, value);
                    root._suppressChildUpdates = false;
                }
            }
        },
        /**
         * Subscribes to parent scope change event
         * @protected
         */
        _subscribeToParentParamChange: function () {
            var self = this;
            if (this.parentScope) {
                //if we have a parent scope and it fires a param change event, we are getting changed too
                this.parentScope.paramChange(function (event, name, value) {
                    //special case when we're setting persistent parameter from a persistent scope should not trigger
                    //change event in child, also we should not listen to persistent param (prefixed) changes as it's
                    //done in a separate handler
                    if (self._findRoot()._suppressChildUpdates || name.indexOf('.') >= 0) {
                        return;
                    }
                    //if a value is getting changed in parent, it needs to be changed in child too
                    if (self.params.hasOwnProperty(name)) {
                        self.params[name] = value;
                    }
                    self._fireParamChangeEvent(name, value, event.source);
                    self._fireChangeEvent(event.source, '_subscribeToParentParamChange', [name]);
                });
            }
        },
        /**
         * Subscribes to root scope change event
         * @protected
         */
        _subscribeToRootParamChange: function () {
            var self = this;
            if (self._persistent) {
                //if we are non-root persistent scope, we need to monitor for prefixed value changes in root
                var root = this._findRoot();
                if (root !== this) {
                    root.paramChange(function (event, name, value) {
                        //special case when we're setting persistent parameter from a persistent scope should not
                        //trigger change event in child
                        if (root._suppressChildUpdates) {
                            return;
                        }
                        //if a prefixed value is getting changed in parent, it needs to be changed in persistent child
                        //too, but without prefix
                        var prefix = self.name + ".";
                        if (name.indexOf(prefix) === 0) {
                            var prefixedName = name.substr(prefix.length);
                            self.params[prefixedName] = value;
                            self._fireParamChangeEvent(prefixedName, value, event.source);
                            self._fireChangeEvent(event.source, '_subscribeToRootParamChange', [name]);
                        }
                    });
                }
            }
        },
        /**
         * Fires a change event to all registered handlers
         * @param sourceScope
         * @private
         */
        _fireChangeEvent: function (sourceScope, eventSource, changedParams) {
            var event = {
                source: sourceScope || null,
                scope: this,
                eventSource: eventSource || null,
                changedParams: changedParams || []
            };
            for (var i = 0; i < this._changeHandlers.length; i++) {
                this._changeHandlers[i].apply(this, [event, this.getParams()]);
            }
        },
        /**
         * Fires a param change event to all registered handlers
         * @param name
         * @param value
         * @param sourceScope
         * @private
         */
        _fireParamChangeEvent: function (name, value, sourceScope) {
            var event = {
                source: sourceScope,
                scope: this,
                changedParams: [name]
            };
            for (var i = 0; i < this._paramChangeHandlers.length; i++) {
                this._paramChangeHandlers[i].apply(this, [event, name, value]);
            }
        }
    };

    return Scope;
});




//////////////////
// WEBPACK FOOTER
// ./~/fcse-state/Scope.js
// module id = 164
// module chunks = 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 23